/**
 * Transaction Wizard designer (obj_type "U") — a guided, step-by-step face on
 * an EXISTING transaction: pick from a list, type a few fields, scan a label,
 * review a grid, confirm, save, print — with branches, steps the system does
 * on its own, related data beside a step, and other wizards called and
 * returned from. The wizard owns the steps; the transaction owns the data, the
 * rules and the save. Design: docs/plans/transaction-wizard.md (§13).
 *
 * Works exactly like the other designers: embedded in the workbench "Design"
 * tab (Save / dirty guard / agent auto-refresh) or full page at
 * /designers/wizard/:project/:objName.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import toast from 'react-hot-toast'
import { ArrowLeft, Loader2, Maximize2, Minimize2, Save, Sparkles, Wand2 } from 'lucide-react'
import { parseApiError } from '@twasta/page-runtime/txn'
import type { EmbeddedDesignerProps } from './embeddedDesigner'
import {
  checkWizard, loadWizard, loadWizardLayout, saveWizard, saveWizardLayout, wizardCatalog,
} from '@/api/wizards'
import type { WizardCatalog, WizardCheckResult, WizardLayout } from '@/api/wizards'
import { useProjectStore } from '@/store/projectStore'
import { useUnsavedChangesGuard } from '@/hooks/useUnsavedChangesGuard'
import { useAgentRefresh } from '@/hooks/useAgentRefresh'
import DiscardChangesModal from '@/components/common/DiscardChangesModal'
import { useProjectPermissions } from '@/auth/useProjectPermissions'
import { ReadOnlyBadge } from '@/auth/ReadOnlyBadge'
import { useRunOnlyRedirect } from '@/auth/useRunOnlyRedirect'
import type { WizardModel } from './wizard/types'
import { blankWizard, sanitizeWizard } from './wizard/types'
import { WizardDesignProvider } from './wizard/context'
import type { WizardDesignCtx } from './wizard/context'
import BaseTab from './wizard/BaseTab'
import StepsTab from './wizard/StepsTab'
import ToolsEditor from './wizard/ToolsEditor'
import CheckTab from './wizard/CheckTab'
import PreviewTab from './wizard/PreviewTab'
import AskAiPanel from './wizard/AskAiPanel'
import WizardFlowDesigner from './wizard/flow/WizardFlowDesigner'
import { SectionHeader, cardStyle, hintStyle } from './wizard/ui'

type Tab = 'base' | 'steps' | 'flow' | 'tools' | 'check' | 'preview'

const TABS: { key: Tab; label: string }[] = [
  { key: 'base', label: 'Base & Mode' },
  { key: 'steps', label: 'Steps' },
  { key: 'flow', label: 'Flow Designer' },
  { key: 'tools', label: 'Tools' },
  { key: 'check', label: 'Check' },
  { key: 'preview', label: 'Preview' },
]

export default function WizardDesignerPage({
  embedded = false,
  hostFullscreen = false,
  embedProject,
  embedObjName,
  onDirtyChange,
  registerSave,
  onSaved,
}: EmbeddedDesignerProps = {}) {
  const params = useParams<{ project: string; objName: string }>()
  const project = embedProject ?? params.project
  useRunOnlyRedirect(project)
  const objName = embedObjName ?? params.objName
  const navigate = useNavigate()
  const location = useLocation()
  const projectName = project || ''
  const objectName = objName || ''
  const { currentProject, selectProject } = useProjectStore()

  useEffect(() => {
    if (project && currentProject?.application.folder_name !== project) {
      selectProject(project)
    }
  }, [project, currentProject, selectProject])

  const [model, setModel] = useState<WizardModel>(() => blankWizard(objectName))
  const [catalog, setCatalog] = useState<WizardCatalog | null>(null)
  const [catalogError, setCatalogError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [lastCheck, setLastCheck] = useState<WizardCheckResult | null>(null)
  const { canEditObjectsOrLoading: canEdit } = useProjectPermissions()
  const [tab, setTab] = useState<Tab>('steps')
  const [selectedStep, setSelectedStep] = useState<string | null>(null)
  const [embedFullscreen, setEmbedFullscreen] = useState(false)
  // Whether the model file exists yet, the "Build with Ask-AI" panel (open by
  // default on a new wizard), and a brief handed to Ask-AI awaiting its write.
  const [exists, setExists] = useState(false)
  const [askOpen, setAskOpen] = useState(false)
  const [askWaiting, setAskWaiting] = useState(false)
  // The saved model when the brief was sent: a reload only counts as Ask-AI's
  // answer when the file differs from it (a question-only turn also reports).
  const askBaseline = useRef<string | null>(null)
  // The Flow Designer's box positions — presentation only, saved beside the
  // model (never inside it), so a move is not an unsaved model change.
  const [layout, setLayout] = useState<WizardLayout>({})
  const [loadNonce, setLoadNonce] = useState(0)
  const layoutTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Dirty tracking — snapshot of the saved model.
  const [savedSnapshot, setSavedSnapshot] = useState<string | null>(null)
  const isDirty = savedSnapshot !== null && JSON.stringify(model) !== savedSnapshot
  const { guard, showDiscardModal, confirmDiscard, cancelDiscard } =
    useUnsavedChangesGuard(isDirty)

  // Reload when the AI agent edits the model file (deferred while dirty).
  const agentRefresh = useAgentRefresh(projectName, isDirty)

  // Only the FIRST load of a wizard shows the full-page loader: an agent
  // reload (after every Ask-AI turn, questions included) must not unmount the
  // open Ask-AI brief or the step being edited.
  const loadedKey = useRef('')

  useEffect(() => {
    let cancelled = false
    if (!projectName || !objectName) return
    const key = `${projectName}/${objectName}`
    const first = loadedKey.current !== key
    if (first) setLoading(true)
    loadWizard(projectName, objectName)
      .then((raw) => {
        if (cancelled) return
        const loaded = raw ? sanitizeWizard(raw, objectName) : blankWizard(objectName)
        const snap = JSON.stringify(loaded)
        setExists(!!raw)
        if (first && !raw) setAskOpen(true)
        loadedKey.current = key
        if (askBaseline.current !== null && raw && snap !== askBaseline.current) {
          askBaseline.current = null
          setAskWaiting(false)
          setAskOpen(false)
          toast.success('Ask-AI updated the wizard — review it below')
        }
        setModel(loaded)
        setSavedSnapshot(snap)
        setLoadNonce((n) => n + 1)
        setSelectedStep((cur) => cur ?? loaded.start ?? loaded.steps[0]?.id ?? null)
      })
      .catch((err) => {
        console.error('Failed to load wizard:', err)
        toast.error('Failed to load wizard')
      })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [projectName, objectName, agentRefresh])

  // The catalog: every transaction / V / D / R / wizard / intent the pickers
  // offer. Re-read with the model (an agent may have added an object).
  useEffect(() => {
    if (!projectName) return
    let cancelled = false
    wizardCatalog(projectName)
      .then((c) => { if (!cancelled) { setCatalog(c); setCatalogError(null) } })
      .catch((err) => {
        if (cancelled) return
        setCatalog(null)
        setCatalogError(parseApiError(err).message || 'The project catalog could not be read')
      })
    return () => { cancelled = true }
  }, [projectName, agentRefresh])

  // The layout, re-read with the model. A file an agent rewrote without one
  // keeps the boxes where they are on screen rather than re-laying them out.
  useEffect(() => {
    if (!projectName || !objectName) return
    let cancelled = false
    loadWizardLayout(projectName, objectName)
      .then((l) => {
        if (cancelled) return
        setLayout((cur) => (l.nodes && Object.keys(l.nodes).length ? l : cur))
      })
      .catch((err) => console.warn('Failed to load the wizard flow layout:', err))
    return () => { cancelled = true }
  }, [projectName, objectName, agentRefresh])

  useEffect(() => () => { if (layoutTimer.current) clearTimeout(layoutTimer.current) }, [])

  // A move is written on its own (debounced) once the wizard has a file; until
  // then it rides along with the first model save.
  const handleLayoutChange = useCallback((next: WizardLayout) => {
    setLayout(next)
    if (!canEdit || !exists || !projectName || !objectName) return
    if (layoutTimer.current) clearTimeout(layoutTimer.current)
    layoutTimer.current = setTimeout(() => {
      saveWizardLayout(projectName, objectName, next).catch((err) => {
        console.error('Failed to save the wizard flow layout:', err)
        toast.error(parseApiError(err).message || 'Failed to save the box positions')
      })
    }, 600)
  }, [canEdit, exists, projectName, objectName])

  // Validate from the Flow Designer: the same Check, over the UNSAVED model.
  const handleValidate = useCallback(async () => {
    if (!projectName) return
    try {
      const res = await checkWizard(projectName, model)
      setLastCheck(res)
      const n = res.errors.length + res.warnings.length
      if (n) toast(`${res.errors.length} error${res.errors.length === 1 ? '' : 's'}, ${res.warnings.length} warning${res.warnings.length === 1 ? '' : 's'} — marked on the boxes`, { icon: '⚠️' })
      else toast.success('No problems found')
    } catch (err) {
      console.error('Wizard check failed', err)
      toast.error(parseApiError(err).message || 'The check could not run')
    }
  }, [projectName, model])

  const handleSave = useCallback(async (): Promise<boolean> => {
    if (!projectName || !objectName) return false
    setSaving(true)
    try {
      if (layoutTimer.current) { clearTimeout(layoutTimer.current); layoutTimer.current = null }
      const hasLayout = !!(layout.nodes && Object.keys(layout.nodes).length)
      const res = await saveWizard(projectName, objectName, model, hasLayout ? layout : null)
      // Keep what is on screen as the saved state; the server's normalised
      // copy may add defaults, which must not read as an unsaved change.
      setSavedSnapshot(JSON.stringify(model))
      setExists(true)
      setLastCheck(res.check)
      const errs = res.check.errors.length
      if (errs) toast(`Saved — ${errs} problem${errs === 1 ? '' : 's'} to fix (see Check)`, { icon: '⚠️' })
      else toast.success('Wizard saved')
      // The workbench gates its Live Preview tab on the model existing.
      onSaved?.()
      return true
    } catch (err) {
      console.error('Save failed', err)
      toast.error(parseApiError(err).message || 'Failed to save wizard')
      return false
    } finally {
      setSaving(false)
    }
  }, [projectName, objectName, model, layout, onSaved])

  useEffect(() => {
    if (!embedded || !onDirtyChange) return
    onDirtyChange(isDirty)
    return () => onDirtyChange(false)
  }, [embedded, isDirty, onDirtyChange])
  useEffect(() => {
    if (!embedded || !registerSave) return
    registerSave(handleSave)
  }, [embedded, registerSave, handleSave])

  // Hand the brief to the Ask-AI panel. Unsaved edits are saved first, so the
  // agent revises what is on screen and the reload is not deferred by them.
  const sendToAskAi = useCallback(async (brief: string) => {
    if (isDirty && !(await handleSave())) return
    askBaseline.current = exists ? JSON.stringify(model) : ''
    window.dispatchEvent(new CustomEvent('twasta:agent-send-prompt', {
      detail: { project: projectName, prompt: brief },
    }))
    setAskWaiting(true)
    toast.success('Sent to Ask-AI — answer its questions in the Ask-AI panel')
  }, [isDirty, handleSave, projectName, exists, model])

  const handleBack = useCallback(() => {
    const selectedNodeId = (location.state as { selectedNodeId?: string })?.selectedNodeId
    guard(() => navigate('/workbench', { state: { selectedNodeId } }))
  }, [guard, location.state, navigate])

  const handleSaveAndContinue = useCallback(async () => {
    const ok = await handleSave()
    if (ok) confirmDiscard()
  }, [handleSave, confirmDiscard])

  const ctx = useMemo<WizardDesignCtx>(() => ({
    project: projectName,
    model,
    catalog,
    base: catalog?.transactions.find((t) => t.obj_name.toLowerCase() === (model.base_object || '').toLowerCase()) || null,
    readOnly: !canEdit,
  }), [projectName, model, catalog, canEdit])

  if (loading) {
    return (
      <div className="flex items-center justify-center" style={{ height: '100%' }}>
        <Loader2 size={20} style={{ animation: 'spin 1s linear infinite' }} />
        <span style={{ marginLeft: 8 }}>Loading wizard…</span>
      </div>
    )
  }

  return (
    <div data-tw-wizard-designer={objectName} style={embedded && embedFullscreen ? {
      position: 'fixed', inset: 0, zIndex: 1000, background: 'var(--color-bg)',
      display: 'flex', flexDirection: 'column', padding: '0 12px',
    } : {
      display: 'flex', flexDirection: 'column', height: '100%',
    }}>
      {/* Toolbar */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '4px 0', flexShrink: 0,
      }}>
        <div className="flex items-center gap-2" style={{ minWidth: 0 }}>
          {!embedded && (
            <button className="btn btn-ghost btn-sm" onClick={handleBack}>
              <ArrowLeft size={16} />
            </button>
          )}
          <Wand2 size={16} style={{ color: 'var(--color-accent)', flexShrink: 0 }} />
          <div style={{
            minWidth: 0, fontSize: '0.9125rem', color: 'var(--color-text)',
            fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}>
            {projectName}
            <span style={{ opacity: 0.4, margin: '0 6px' }}>/</span>
            {objectName}
            <span style={{
              marginLeft: 8, fontSize: '0.8125rem', fontWeight: 400,
              color: 'var(--color-text-secondary)', opacity: 0.8,
            }}>
              wizard{model.base_object ? ` on ${model.base_object}` : ''}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {canEdit && (
            <button
              className={`btn btn-sm ${askOpen ? 'btn-secondary' : 'btn-ghost'}`}
              onClick={() => setAskOpen((o) => !o)}
              title="Describe the wizard and let Ask-AI build or revise it"
              data-tw-action="wizard-askai"
            >
              <Sparkles size={14} />
              Ask-AI
            </button>
          )}
          {canEdit ? (
            <button
              className={`btn btn-sm ${isDirty ? 'btn-primary' : 'btn-secondary'}`}
              onClick={handleSave}
              disabled={saving}
              title={isDirty ? 'You have unsaved changes' : 'No changes to save'}
              style={isDirty ? { boxShadow: '0 0 0 3px rgba(76,141,255,0.35)' } : undefined}
            >
              {saving
                ? <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />
                : <Save size={14} />}
              {saving ? 'Saving…' : isDirty ? 'Save *' : 'Save'}
            </button>
          ) : (
            <ReadOnlyBadge />
          )}
          {embedded && !hostFullscreen && (
            <button
              className="btn btn-ghost btn-sm"
              onClick={() => setEmbedFullscreen((f) => !f)}
              title={embedFullscreen ? 'Restore' : 'Full screen'}
            >
              {embedFullscreen ? <Minimize2 size={15} /> : <Maximize2 size={15} />}
            </button>
          )}
        </div>
      </div>

      {/* Tab bar */}
      <div style={{
        display: 'flex', gap: 4,
        borderTop: '1px solid var(--color-border)',
        borderBottom: '1px solid var(--color-border)',
        background: 'var(--color-bg-secondary)', padding: '0 8px',
      }}>
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            style={{
              padding: '8px 14px', border: 'none', background: 'transparent',
              borderBottom: tab === t.key
                ? '2px solid var(--color-accent)' : '2px solid transparent',
              fontSize: 14, fontWeight: tab === t.key ? 600 : 400,
              color: tab === t.key ? 'var(--color-text)' : 'var(--color-text-secondary)',
              cursor: 'pointer',
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Body — the Flow Designer fills it; every other tab scrolls. */}
      <div style={tab === 'flow' ? {
        flex: 1, minHeight: 0, overflow: 'hidden', padding: 8, background: 'var(--color-bg)',
        display: 'flex', flexDirection: 'column', gap: 8,
      } : { flex: 1, overflowY: 'auto', padding: 16, background: 'var(--color-bg)' }}>
        <WizardDesignProvider value={ctx}>
          {catalogError && (
            <div style={{ ...cardStyle, ...hintStyle, color: 'var(--color-warning, #b45309)' }}>
              {catalogError} — the pickers fall back to typed names.
            </div>
          )}
          {model.base_object && catalog && !ctx.base && (
            <div style={{ ...cardStyle, ...hintStyle, color: 'var(--color-error, #d33)' }}>
              The base transaction “{model.base_object}” is not in this project.
            </div>
          )}

          {askOpen && canEdit && (
            <AskAiPanel objName={objectName} exists={exists} waiting={askWaiting}
              onSend={(brief) => { void sendToAskAi(brief) }}
              onClose={() => setAskOpen(false)} />
          )}

          {tab === 'base' && <BaseTab onChange={setModel} />}

          {tab === 'steps' && (
            <StepsTab onChange={setModel} selected={selectedStep} onSelect={setSelectedStep} />
          )}

          {tab === 'flow' && (
            <div style={{ flex: 1, minHeight: 0 }}>
              <WizardFlowDesigner
                onChange={setModel}
                selected={selectedStep}
                onSelect={setSelectedStep}
                layout={layout}
                onLayoutChange={handleLayoutChange}
                check={lastCheck}
                onValidate={handleValidate}
                historyKey={loadNonce}
              />
            </div>
          )}

          {tab === 'tools' && (
            <div style={cardStyle}>
              <SectionHeader>Wizard tools — on every step (subject to “available when”)</SectionHeader>
              <ToolsEditor tools={model.tools}
                onChange={(tools) => setModel((m) => {
                  const next = { ...m, tools }
                  if (!tools) delete next.tools
                  return next
                })} />
            </div>
          )}

          {tab === 'check' && (
            <CheckTab lastSaveCheck={lastCheck}
              onGoToStep={(step) => { setSelectedStep(step); setTab('steps') }} />
          )}

          {tab === 'preview' && (
            <PreviewTab
              project={projectName}
              objName={objectName}
              inputs={model.inputs || []}
              baseObject={model.base_object}
              dirty={isDirty}
              saving={saving}
              onSave={handleSave}
              canEdit={canEdit}
            />
          )}
        </WizardDesignProvider>
      </div>

      <DiscardChangesModal
        open={showDiscardModal}
        onCancel={cancelDiscard}
        onDiscard={confirmDiscard}
        onSave={handleSaveAndContinue}
        saving={saving}
      />
    </div>
  )
}
